import React, { FC, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";

import StepIntro from "../../../StepIntro";
import StepIntroInfo from "../../../StepIntroInfo";
import StepIntroInfoFoot from "../../../StepIntroInfoFoot";
import Camera from "../../../Camera";
import StepScanResult from "../../../StepScanResult";
import StepLoader from "../../../StepLoader";
import Result from "../../../Result";
import ResultError from "../../../ResultError";
import StepScanSelect from "../../../StepScanSelect";
import StepGenderSelect from "../../../StepGenderSelect";

import { putS3, visionTrigger } from "../../../../api/endpoints";
import { GENDERS, MODAL_STEPS } from "../../../../constants/modal";
import { subtitlesStyles, font, uxGender } from "../../../../configs";

import {
  countScanAttempt,
  trackScanFlow,
} from "../../../../utils/flowTracking";

import "./index.css";

interface IPropsSteps {
  step: any;
  setStep: (
    value: React.SetStateAction<{
      number: number;
    }>
  ) => void;
  photo: string | null;
  scanImageUrl: string | null;
  recommendation: any | null;
  triggerStarted: boolean;
  setTriggerStarted: React.Dispatch<boolean>;
  restart: () => void;
  handleOrientation: (e: DeviceOrientationEvent) => void;
  setShowSpinner: React.Dispatch<boolean>;
  isReady: boolean;
}

const Steps: FC<IPropsSteps> = ({
  step,
  setStep,
  photo,
  scanImageUrl,
  recommendation,
  triggerStarted,
  setTriggerStarted,
  handleOrientation,
  restart,
  setShowSpinner,
  isReady,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const [error, setError] = useState<boolean>(false);

  const [isProcessing, setIsProcessing] = useState(false);

  const sizeValue = useRef<string | null>("");
  const [sizeCountry, setSizeCountry] = useState<string>("EU");

  const urlParameters = new URLSearchParams(window.location.search);
  const user_id = urlParameters.get("uid");
  const genderFromUrl = urlParameters.get("gender");

  const resolveGender = (value: string | null) => {
    if (!value) return "";
    return value === "male" ? GENDERS.M : GENDERS.F;
  };

  const initialGender = resolveGender(genderFromUrl) || resolveGender(uxGender);

  const [selectedGender, setSelectedGender] = useState(initialGender);

  const handleInitTrigger = async (user_id: string | null) => {
    if (user_id) {
      setTriggerStarted(true);

      const visionCheckBody = {
        user: {
          id: user_id,
          answers: {
            usual_size: {
              value: sizeValue.current,
              system: sizeCountry,
            },
            gender: selectedGender,
          },
        },
      };

      const result = await visionTrigger(visionCheckBody);
      let res: boolean;
      if (result.success) {
        trackScanFlow("trigger_ok", { step: "SIZE_SELECT" });
        setError(false);
        res = true;
      } else {
        // The photo is judged only now, after the gender and size screens.
        trackScanFlow("trigger_failed", {
          step: "SIZE_SELECT",
          reason: result.reason,
        });
        setError(true);
        res = false;
      }
      setTriggerStarted(false);
      return res;
    }
  };

  const handleNextLoaderStep = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    const validation = await handleInitTrigger(user_id);
    setIsProcessing(false);
    if (!validation) setStep(MODAL_STEPS.ERROR);
    if (validation) {
      setTimeout(() => window.close(), 1000);
      // window.close() only closes a tab opened by a script: opened from a QR
      // code, the page stays on screen with no word that the scan is done.
      setTimeout(() => {
        if (!window.closed) {
          trackScanFlow("close_failed", { step: "SIZE_SELECT" });
        }
      }, 2500);
    }
  };

  const handleSendPhoto = async (scanImageUrl: string, photo: any) => {
    const result = await putS3(scanImageUrl, photo).then((res: any) => res);
    return result;
  };

  const handleSendImageToS3 = async () => {
    if (scanImageUrl && photo) {
      setShowSpinner(true);
      const uploadStart = Date.now();
      const result = await handleSendPhoto(scanImageUrl, photo);
      setShowSpinner(false);

      if (result) {
        trackScanFlow("photo_uploaded", {
          step: "SCAN_2",
          value: Date.now() - uploadStart,
        });
        setStep(MODAL_STEPS.SCAN_RESULT);
      } else {
        trackScanFlow("photo_upload_failed", {
          step: "SCAN_2",
          reason: "put_failed",
        });
        setStep(MODAL_STEPS.ERROR);
      }
    } else {
      trackScanFlow("photo_upload_failed", {
        step: "SCAN_2",
        reason: !scanImageUrl ? "no_upload_url" : "no_photo",
      });
      setStep(MODAL_STEPS.ERROR);
    }
  };

  // The gender answered (or preset by the URL / the brand).
  useEffect(() => {
    if (!selectedGender) return;
    trackScanFlow("gender_selected", {
      step: "GENDER_SELECT",
      value: selectedGender,
    });
  }, [selectedGender]);

  const descriptionStyles = {
    color: "#2E2E2E",
    fontSize: subtitlesStyles.fontSize as React.CSSProperties["fontSize"],
    fontWeight: 400,
    textAlign: subtitlesStyles.textAlign as React.CSSProperties["textAlign"],
    textTransform:
      subtitlesStyles.textTransform === "capitalize"
        ? "none"
        : subtitlesStyles.textTransform,
    justifyContent:
      subtitlesStyles.textAlign as React.CSSProperties["justifyContent"],
    fontFamily: isMobile
      ? `${font}, sans-serif`
      : `${font}, sans-serif !important`,
    lineHeight: "17px",
    margin: `${isMobile ? "20px" : "30px"} 0px 40px 0px`,
  };

  return (
    <>
      {step?.number === MODAL_STEPS.INTRO.number && (
        <StepIntro
          step={step}
          descriptionStyles={descriptionStyles}
          nextStep={() => setStep(MODAL_STEPS.INTRO_INFO)}
        />
      )}
      {step?.number === MODAL_STEPS.INTRO_INFO.number && (
        <StepIntroInfo
          step={step}
          descriptionStyles={descriptionStyles}
          nextStep={() => setStep(MODAL_STEPS.INTRO_INFO_FOOT)}
        />
      )}
      {step?.number === MODAL_STEPS.INTRO_INFO_FOOT.number && (
        <StepIntroInfoFoot
          step={step}
          descriptionStyles={descriptionStyles}
          nextStep={() => setStep(MODAL_STEPS.SCAN_1)}
        />
      )}
      {step?.number === MODAL_STEPS.SCAN_1.number && (
        <Camera
          step={step}
          photo={photo}
          handleSendImage={handleSendImageToS3}
          nextStep={() => setStep(MODAL_STEPS.SCAN_2)}
          handleOrientation={handleOrientation}
          isReady={isReady}
        />
      )}
      {step?.number === MODAL_STEPS.SCAN_2.number && (
        <Camera
          step={step}
          photo={photo}
          handleSendImage={handleSendImageToS3}
          nextStep={() => setStep(MODAL_STEPS.SCAN_RESULT)}
          handleOrientation={handleOrientation}
          isReady={isReady}
        />
      )}
      {step?.number === MODAL_STEPS.SCAN_RESULT.number && (
        <StepScanResult
          triggerStarted={triggerStarted}
          error={error}
          photo={photo}
          restart={() => {
            trackScanFlow("photo_retaken", { step: "SCAN_RESULT" });
            setError(false);
            restart();
          }}
          nextStep={() => {
            trackScanFlow("photo_accepted", { step: "SCAN_RESULT" });
            setStep(
              genderFromUrl ? MODAL_STEPS.SIZE_SELECT : MODAL_STEPS.GENDER_SELECT
            );
          }}
        />
      )}
      {step?.number === MODAL_STEPS.GENDER_SELECT.number && (
        <StepGenderSelect
          selectedGender={selectedGender}
          setSelectedGender={setSelectedGender}
          nextStep={() => setStep(MODAL_STEPS.SIZE_SELECT)}
          descriptionStyles={descriptionStyles}
        />
      )}
      {step?.number === MODAL_STEPS.SIZE_SELECT.number && (
        <StepScanSelect
          step={step}
          value_1={sizeValue}
          sizeCountry={sizeCountry}
          setSizeCountry={setSizeCountry}
          nextStep={async () => {
            trackScanFlow("size_selected", {
              step: "SIZE_SELECT",
              value: `${sizeValue.current ?? ""} ${sizeCountry}`.trim(),
            });
            await handleNextLoaderStep();
          }}
        />
      )}
      {step?.number === MODAL_STEPS.LOADER.number && <StepLoader />}
      {step?.number === MODAL_STEPS.ERROR.number && (
        <ResultError
          step={step}
          nextStep={() => {
            trackScanFlow("error_retry", { step: "ERROR" });
            countScanAttempt();
            setStep(MODAL_STEPS.INTRO);
          }}
        />
      )}
      {step?.number === MODAL_STEPS.RESULT.number && (
        <Result step={step} restart={restart} recommendation={recommendation} />
      )}
    </>
  );
};

export default Steps;
